// src/pages/Checkout.js

import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { isFreeEventCost } from "../utils/eventCost";
import { executeCaptcha } from "../utils/recaptcha";
import EventRegistration from "./EventRegistration";
import EventPayment from "./EventPayment";

export default function Checkout() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");

    const [basicValues, setBasicValues] = useState({
        name: "",
        email: "",
        website: "",
        paymentMethod: "",
    });

    // Stores answers to custom questions: { [question.id]: value }
    const [formAnswers, setFormAnswers] = useState({});

    const [step, setStep] = useState("registration"); // "registration" | "payment"
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");

    // ──────────────────────────────────────────────
    // Load event by slug
    // ──────────────────────────────────────────────
    useEffect(() => {
        let cancelled = false;

        async function fetchEvent() {
            setLoading(true);
            setLoadError("");

            try {
                const res = await fetch(
                    `/.netlify/functions/get-event-by-slug?slug=${encodeURIComponent(
                        slug
                    )}`
                );

                if (!res.ok) {
                    throw new Error("Unable to load event.");
                }

                const data = await res.json();
                if (!cancelled) {
                    // some functions return { event }, some just event
                    setEvent(data.event || data);
                }
            } catch (err) {
                console.error(err);
                if (!cancelled) {
                    setLoadError(
                        err.message || "Something went wrong loading this event."
                    );
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        if (slug) fetchEvent();

        return () => {
            cancelled = true;
        };
    }, [slug]);

    // ──────────────────────────────────────────────
    // Derived flags + helpers
    // ──────────────────────────────────────────────
    const isFree = isFreeEventCost(event?.cost);
    const paymentsEnabled = process.env.REACT_APP_ENABLE_PAYMENTS === "true";
    const shouldCollectPayment = paymentsEnabled && !isFree;

    const amount = useMemo(() => {
        if (isFree) return 0;
        const raw = Number(event?.cost?.amount ?? 0);
        return Number.isNaN(raw) ? 0 : raw;
    }, [event, isFree]);

    const currency = event?.cost?.currency || "CAD";

    const customForm = event?.customForm;
    const hasCustomForm =
        customForm?.enabled &&
        Array.isArray(customForm?.questions) &&
        customForm.questions.length > 0;
    const customQuestions = useMemo(
        () => (hasCustomForm ? customForm.questions : []),
        [customForm, hasCustomForm]
    );

    const summaryFields = useMemo(() => {
        const base = [
            { label: "Name", value: basicValues.name },
            { label: "Email", value: basicValues.email },
        ];

        const extras =
            customQuestions
                ?.map((q) => {
                    const key = q.id || q.label;
                    return {
                        label: q.label,
                        value: formAnswers[key],
                    };
                })
                .filter((f) => f.value != null && f.value !== "") || [];

        return [...base, ...extras];
    }, [basicValues, customQuestions, formAnswers]);

    // ──────────────────────────────────────────────
    // Handlers
    // ──────────────────────────────────────────────
    const handleBasicChange = (e) => {
        const { name, value } = e.target;
        setBasicValues((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const updateAnswer = (id, value) => {
        setFormAnswers((prev) => ({
          ...prev,
          [id]: value,
        }));
      };

    const handlePaymentMethodSelect = (methodId) => {
        setBasicValues((prev) => ({
            ...prev,
            paymentMethod: methodId,
        }));
        setSubmitError("");
    };

    const renderQuestion = (q) => {
        const id = q.id || q.label || "";
        if (!id) return null;
      
        const required = !!q.required;
        const type = q.type || "short-text";
        const options = Array.isArray(q.options) ? q.options : [];
        const answer = formAnswers[id];
        const labelText = q.label || "Untitled question";
      
        // Helper for options that might be strings or { label, value }
        const getOptValue = (opt) =>
          typeof opt === "string" ? opt : opt.value ?? opt.label ?? "";
        const getOptLabel = (opt) =>
          typeof opt === "string" ? opt : opt.label ?? opt.value ?? "";
      
        // SHORT TEXT
        if (type === "short-text") {
          return (
            <div key={id} className="checkout-form-field">
              <label className="checkout-form-label" htmlFor={`q-${id}`}>
                {labelText}
                {required && <span className="checkout-form-required">*</span>}
              </label>
              <input
                id={`q-${id}`}
                type="text"
                className="checkout-form-input"
                required={required}
                value={answer || ""}
                onChange={(e) => updateAnswer(id, e.target.value)}
              />
            </div>
          );
        }
      
        // LONG TEXT
        if (type === "long-text") {
          return (
            <div key={id} className="checkout-form-field">
              <label className="checkout-form-label" htmlFor={`q-${id}`}>
                {labelText}
                {required && <span className="checkout-form-required">*</span>}
              </label>
              <textarea
                id={`q-${id}`}
                className="checkout-form-input"
                required={required}
                rows={4}
                value={answer || ""}
                onChange={(e) => updateAnswer(id, e.target.value)}
              />
            </div>
          );
        }
      
        // MULTIPLE-CHOICE (radios)
        if (type === "multiple-choice") {
          return (
            <div key={id} className="checkout-form-field">
              <span className="checkout-form-label">
                {labelText}
                {required && <span className="checkout-form-required">*</span>}
              </span>
              <div className="checkout-form-options">
                {options.map((opt, index) => {
                  const optValue = getOptValue(opt);
                  const optLabel = getOptLabel(opt);
                  return (
                    <label
                      key={`${id}-opt-${index}`}
                      className="checkout-form-option"
                    >
                      <input
                        type="radio"
                        name={`q-${id}`}
                        value={optValue}
                        required={required}
                        checked={answer === optValue}
                        onChange={(e) => updateAnswer(id, e.target.value)}
                      />
                      <span>{optLabel}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        }
      
        // CHECKBOXES (multi-select)
        if (type === "checkboxes") {
          const selected = Array.isArray(answer) ? answer : [];
      
          const toggleOption = (opt) => {
            const optValue = getOptValue(opt);
            const next = selected.includes(optValue)
              ? selected.filter((o) => o !== optValue)
              : [...selected, optValue];
            updateAnswer(id, next);
          };
      
          return (
            <div key={id} className="checkout-form-field">
              <span className="checkout-form-label">
                {labelText}
                {required && <span className="checkout-form-required">*</span>}
              </span>
              <div className="checkout-form-options">
                {options.map((opt, index) => {
                  const optValue = getOptValue(opt);
                  const optLabel = getOptLabel(opt);
                  return (
                    <label
                      key={`${id}-opt-${index}`}
                      className="checkout-form-option"
                    >
                      <input
                        type="checkbox"
                        name={`q-${id}`}
                        value={optValue}
                        checked={selected.includes(optValue)}
                        onChange={() => toggleOption(opt)}
                      />
                      <span>{optLabel}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        }
      
        // FALLBACK → treat as short-text
        return (
          <div key={id} className="checkout-form-field">
            <label className="checkout-form-label" htmlFor={`q-${id}`}>
              {labelText}
              {required && <span className="checkout-form-required">*</span>}
            </label>
            <input
              id={`q-${id}`}
              type="text"
              className="checkout-form-input"
              required={required}
              value={answer || ""}
              onChange={(e) => updateAnswer(id, e.target.value)}
            />
          </div>
        );
      };
      

    // Registration endpoint (free now, paid-ready later)
    const registerForEvent = async () => {
        const captchaToken = await executeCaptcha("register_free_event");

        const res = await fetch("/.netlify/functions/registration-free-event", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                slug,
                basicValues,
                formAnswers,
                captchaToken,
            }),
        });

        const data = await res.json();
        if (!res.ok) {
            throw new Error(data.error || "Unable to complete registration.");
        }
        return data;
    };

    // Paid: create checkout session endpoint
    const createPaidCheckoutSession = async () => {
        const res = await fetch("/.netlify/functions/create-checkout-session", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                slug,
                amount,
                currency,
                paymentMethod: basicValues.paymentMethod,
                basicValues,
                formAnswers,
            }),
        });

        const data = await res.json();
        if (!res.ok || !data.url) {
            throw new Error(data.error || "Unable to start payment.");
        }
        return data;
    };

    // Main form submit (behaves differently per step)
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError("");

        if (!basicValues.name || !basicValues.email) {
            setSubmitError("Please enter your name and email.");
            return;
        }

        if (step === "registration") {
            if (!shouldCollectPayment) {
                try {
                    setSubmitting(true);
                    const data = await registerForEvent();
                    navigate(buildReturnUrl(location.search, slug, data.status), {
                        replace: true,
                    });
                } catch (err) {
                    console.error(err);
                    setSubmitError(err.message || "Unable to complete registration.");
                } finally {
                    setSubmitting(false);
                }
                return;
            }

            // Paid event: move to review + payment step
            setStep("payment");
            return;
        }

        if (step === "payment") {
            if (!isFree && !basicValues.paymentMethod) {
                setSubmitError("Please choose a payment method.");
                return;
            }

            try {
                setSubmitting(true);
                const data = await createPaidCheckoutSession();
                window.location.href = data.url; // redirect to Stripe/processor
            } catch (err) {
                console.error(err);
                setSubmitError(
                    err.message || "Something went wrong starting payment."
                );
            } finally {
                setSubmitting(false);
            }
        }
    };

    // ──────────────────────────────────────────────
    // Render
    // ──────────────────────────────────────────────
    if (loading) {
        return (
            <div className="page page--contained checkout-page">
                <p className="page-status checkout-loading-text">Loading event…</p>
            </div>
        );
    }

    if (loadError || !event) {
        return (
            <div className="page page--contained checkout-page">
                <p className="page-status checkout-error-text">
                    {loadError || "This event could not be found."}
                </p>
            </div>
        );
    }

    return (
        <div className="page page--contained checkout-page">
            <section className="checkout-main-layout">
                <div className="checkout-main-primary">
                    <header className="page-header page-header--flush checkout-header">
                        <h1 className="page-title checkout-title">
                            {step === "registration"
                                ? `Register for “${event.name}”`
                                : "Review & Pay"}
                        </h1>

                        <p className="page-subtitle checkout-subtitle">
                            {step === "registration"
                                ? shouldCollectPayment
                                    ? "Enter your information to reserve your spot."
                                    : "Enter your information to register."
                                : "Confirm your details and choose a payment method."}
                        </p>
                    </header>


                    <form className="checkout-modal-form" onSubmit={handleSubmit}>
                        {step === "registration" && (
                            <>
                                <EventRegistration
                                    basicValues={basicValues}
                                    onBasicChange={handleBasicChange}
                                    hasCustomForm={hasCustomForm}
                                    customQuestions={customQuestions}
                                    renderQuestion={renderQuestion}
                                />

                                {submitError && (
                                    <p className="checkout-error-text">{submitError}</p>
                                )}

                                <button
                                    type="submit"
                                    className="btn checkout-form-submit"
                                    disabled={submitting}
                                >
                                    {shouldCollectPayment
                                        ? "Review Order"
                                        : "Complete registration"}
                                </button>
                            </>
                        )}

                        {step === "payment" && (
                            <EventPayment
                                isFree={isFree}
                                event={event}
                                amount={amount}
                                currency={currency}
                                summaryFields={summaryFields}
                                selectedMethod={basicValues.paymentMethod}
                                onSelectMethod={handlePaymentMethodSelect}
                                submitting={submitting}
                                submitError={submitError}
                                onBack={() => setStep("registration")}
                            />
                        )}
                    </form>
                </div>
            </section>
        </div>
    );
}

function buildReturnUrl(search, slug, status) {
    const params = new URLSearchParams(search);
    const requested = params.get("returnTo");
    const fallback = `/event/${encodeURIComponent(slug)}`;
    const targetPath = requested && requested.startsWith("/") ? requested : fallback;

    const url = new URL(targetPath, window.location.origin);
    url.searchParams.set("registered", "1");
    if (status) {
        url.searchParams.set("registration", status);
    }
    return `${url.pathname}${url.search}`;
}
